import { SHOPS } from "../constants";
import { resolveRouterPath } from "../router";
import { encodeProductId } from "./helpers";
import { BrandStats, CategoryStats, Price, ProductResult, ProductSearchResult, ProductSuggestion } from "./models";

const oneDay = 24 * 60 * 60 * 1000;

// helper functions
const parseIsoDateString = (dateString: string | null | undefined, fallback: number = new Date().getTime()): number => {
    try {
        if (!dateString) return fallback;
        else return new Date(dateString).getTime();
    } catch (err) {
        return fallback;
    }
}

const startOfDay = (date: number): number => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.getTime();
}

const endOfDay = (date: number): number => { // === 0:0AM next day
    const endOfDay = new Date(date + oneDay);
    endOfDay.setHours(0, 0, 0, 0);
    return endOfDay.getTime();
}

const durationInDays = (startOfDay: number, endOfDay: number) => {
    const diff = endOfDay - startOfDay;
    return Math.abs(Math.round(diff / oneDay));
}

const cleanPromo = (promo: string | null | undefined): string | null => {
    if (!!promo && promo !== "None") {
        return promo;
    }
    return null;
}

const unitPrice = (priceInfo: ProductSearchResult | Price): string | null => {
    if (!!priceInfo.measurementunitprice && priceInfo.measurementunit && ['KG', 'L', 'STUK', 'STUK*'].includes(priceInfo.measurementunit)) {
        return `€ ${priceInfo.measurementunitprice}/${priceInfo.measurementunit}`;
    }
    return null;
}

const quantity = (priceInfo: ProductSearchResult | Price): string | null => {
    if (!!priceInfo.totalmeasurementunits && !!priceInfo.piecemeasurementunit) {
        return `${priceInfo.totalmeasurementunits} ${priceInfo.piecemeasurementunit}`;
    }
    if (!!priceInfo.quantitystring && priceInfo.quantitystring !== "None") {
        return priceInfo.quantitystring;
    }
    return null;
}

const promoPrice = (priceInfo: ProductSearchResult | Price): number | null => {
    if (!priceInfo.promo || priceInfo.promo === "None") {
        return null;
    } else if (!!priceInfo.price && priceInfo.promoarticlecount == 1 && !!priceInfo.originalprice) {
        const promoPrice = priceInfo.price;
        if (promoPrice > 0 && promoPrice < 9999) return promoPrice;
    } else if (!!priceInfo.promodiscountpercent && !!priceInfo.price) {
        const promoPrice = ((100 - priceInfo.promodiscountpercent) * (priceInfo.price)) / 100;
        if (promoPrice > 0 && promoPrice < 9999) return promoPrice;
    }
    return null;
}

const promoQuantity = (priceInfo: ProductSearchResult | Price): string | null => {
    const pp = promoPrice(priceInfo);
    if (priceInfo.promoarticlecount == 1 && priceInfo.originalprice) return `directe korting`
    if (!pp && !priceInfo.promoarticlecount) return null;
    return `per ${priceInfo.promoarticlecount} stuks`;
}

const promoPercentage = (priceInfo: ProductSearchResult | Price): number | null => {
    if (!!priceInfo.promodiscountpercent) {
        return priceInfo.promodiscountpercent / 100;
    }
    return null;
}

const sortPrices = (prices: Price[], ascending: boolean = true): Price[] => {
    return prices.sort((a, b) => {
        const timeDiff = parseIsoDateString(a.publish_time_first) - parseIsoDateString(b.publish_time_first);
        return ascending ? timeDiff : -timeDiff;
    });
}

export const getProductImageUrl = (shopId: string | undefined, productRef: string | undefined, size: number = 200): string => {
    if (!shopId || !productRef) return '/img/no-image.jpg';
    const index = Object.keys(SHOPS).indexOf(shopId);
    if (index < 0) return '/img/no-image.jpg';
    return `/api/product/${encodeProductId(shopId, productRef)}/img/${size}`
    // return `/productimg/${index}-${productRef}-${size}x${size}.jpg`;
};

export const getProductUrl = (shopId: string | undefined, productRef: string | undefined, product_name: string | undefined): string => {
    if (!shopId || !productRef) return resolveRouterPath(""); // TODO: MAKE 404 PAGE
    const index = Object.keys(SHOPS).indexOf(shopId);
    if (index < 0) return resolveRouterPath(""); // TODO: MAKE 404 PAGE
    return `/productid/${encodeProductId(shopId, productRef)}/${product_name || `${SHOPS[shopId as keyof typeof SHOPS]} | Product Page`}`;
};

const transformPrice = (data: Price): Price => {
    const first = parseIsoDateString(data.publish_time_first);
    const last = parseIsoDateString(data.publish_time_last);
    const start = startOfDay(first);
    const end = endOfDay(last);
    return {
        ...data,
        promo: cleanPromo(data.promo),
        firstPublished: first,
        lastPublished: last,
        startOfDay: start,
        endOfDay: end,
        durationInDays: durationInDays(start, end),
        unitPrice: unitPrice(data),
        quantity: quantity(data),
        promoPrice: promoPrice(data),
        promoQuantity: promoQuantity(data),
        promoPercentage: promoPercentage(data),
    }
}

const transformPrices = (prices: Price[]): Price[] => {
    const ret = prices.filter(price =>
        !!price.price
        && !!price.publish_time_first
        && !!price.publish_time_last).map(data => transformPrice(data));

    return sortPrices(ret);
}

export const transformProductSearchResult = (data: ProductSearchResult): ProductSearchResult => {
    // console.info(`setting promoquantity to ${promoQuantity(data)} for ${data.product_name}`);
    const first = parseIsoDateString(data.first_scraped);
    const last = parseIsoDateString(data.last_scraped);
    const start = startOfDay(first);
    const end = endOfDay(last);
    return {
        ...data,
        productId: encodeProductId(data.shop || "", data.product_ref || ""),
        promo: cleanPromo(data.promo),
        firstScraped: first,
        startOfDay: start,
        lastScraped: last,
        endOfDay: end,
        durationInDays: durationInDays(start, end),
        unitPrice: unitPrice(data),
        quantity: quantity(data),
        promoPrice: promoPrice(data),
        promoQuantity: promoQuantity(data),
        promoPercentage: promoPercentage(data),
        imageUrl: getProductImageUrl(data.shop, data.product_ref),
        productUrl: getProductUrl(data.shop, data.product_ref, data.product_name)
    }
}


export const transformProductResponse = (response: ProductResult): ProductResult => {

    const transformedPrices = transformPrices(response.prices);
    if (!transformedPrices.length) return response;
    const promoDays = transformedPrices.reduce((accumulator, currentValue) => accumulator + (!!currentValue.promo ? currentValue.durationInDays : 0), 0);
    const nonPromoDays = transformedPrices.reduce((accumulator, currentValue) => accumulator + (!currentValue.promo ? currentValue.durationInDays : 0), 0);
    const firstScraped = parseIsoDateString(response.first_scraped);
    const lastScraped = parseIsoDateString(response.last_scraped);
    const firstPublished: number = transformedPrices.length ? transformedPrices[0].firstPublished! : new Date().getTime();
    const lastPublished: number = transformedPrices.length ? transformedPrices[transformedPrices.length - 1].lastPublished! : new Date().getTime()
    const sod: number = startOfDay(firstPublished);
    const eod: number = endOfDay(lastPublished);
    return {
        ...response,
        prices: transformedPrices,
        promoPrices: transformedPrices.filter(price => !!price.promoPrice),
        firstScraped: firstScraped,
        lastScraped: lastScraped,
        firstPublished: firstPublished,
        lastPublished: lastPublished,
        startOfDay: sod,
        endOfDay: eod,
        durationInDays: durationInDays(sod, eod),
        currentPrice: transformedPrices.length > 0 ? transformedPrices[transformedPrices.length - 1] : null,
        previousPrice: transformedPrices.length > 1 ? transformedPrices[transformedPrices.length - 2] : null,
        imageUrl: getProductImageUrl(response.shop, response.product_ref),
        productUrl: getProductUrl(response.shop, response.product_ref, response.product_name),
        promoDays: promoDays,
        nonPromoDays: nonPromoDays,
        maxDiscountPercentage: Math.max(...transformedPrices.map(p => p.promodiscountpercent || 0)),
        maxPrice: Math.max(...transformedPrices.map(p => p.price || 0)),
        minPrice: Math.min(...transformedPrices.map(p => p.price || 0)),
        maxOriginalPrice: Math.max(...transformedPrices.map(p => p.originalprice || 0)),
        maxMeasurementUnitPrice: Math.max(...transformedPrices.map(p => p.measurementunitprice || 0)),
    };

}

export const calculateSearchResultStats = (
    results: ProductSuggestion[]
): { categoryStats: Record<string, CategoryStats>, brandStats: Record<string, BrandStats> } => {

    // Create objects to aggregate stats
    const categoryStats: Record<string, CategoryStats> = {};
    const brandStats: Record<string, BrandStats> = {};

    // Process each result
    const productSuggestions: ProductSuggestion[] = results as ProductSuggestion[];
    productSuggestions.forEach(result => {
        // Handle categories
        (result?.categories || []).forEach(category => {
            if (!categoryStats[category]) {
                categoryStats[category] = {
                    category: category,
                    level: 0,
                    product_count: 1
                };
            } else {
                categoryStats[category].product_count++;
            }
        });

        // Handle brands
        if (result.brand) {
            if (!brandStats[result.brand]) {
                brandStats[result.brand] = {
                    brand: result.brand,
                    product_count: 1
                };
            } else {
                brandStats[result.brand].product_count++;
            }
        }
    });

    return {
        categoryStats,
        brandStats
    };
}

export const mapCategoryStats = (results: CategoryStats[]): Record<string, CategoryStats> => {
    const categoryStats: Record<string, CategoryStats> = {};
    results.forEach(result => {
        categoryStats[result.category] = result;
    })
    return categoryStats;
}